import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import clone from "lodash-es/clone.js";
import { IPlace } from "./models/Models";
import throttle from "lodash-es/throttle.js";

export function makeCustomEvent(name: string, details: object = {}) {
    return new CustomEvent(name, {
        bubbles: true,
        composed: true,
        detail: details,
    });
}

// Always escape HTML for text arguments!
export function escapeHtml(html: string) {
    const div = document.createElement("div");
    div.textContent = html;
    return div.innerHTML;
}

// Custom function to emit toast notifications
export function showToast(
    message: string,
    variant = "primary",
    icon = "info-circle",
    duration = 3000
) {
    const alert = Object.assign(document.createElement("sl-alert"), {
        variant,
        closable: true,
        duration: duration,
        innerHTML: `
        <sl-icon name="${icon}" slot="icon"></sl-icon>
        ${escapeHtml(message)}
      `,
    });

    document.body.append(alert);
    return (alert as any).toast();
}

export function validateEmail(email: string | undefined): boolean {
    if (!email) {
        return false;
    }
    return !!email.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
}

export function isAscii(str: string) {
    if (typeof str !== "string") {
        return false;
    }
    for (let i = 0; i < str.length; i++) {
        if (str.charCodeAt(i) > 127) {
            return false;
        }
    }
    return true;
}

export function httpGetJson(url: string): Promise<string> {
    // Return a new promise.
    return new Promise(function (resolve, reject) {
        window
            .fetch(url, {
                method: "GET",
                mode: "cors",
                headers: {
                    "Cache-Control": "max-age=1000",
                    //"content-type": "application/json",
                },
            })
            .then((response) => {
                if (response.status !== 200) {
                    reject({ status: response.status, msg: response.statusText });
                } else {
                    resolve(response.json());
                }
            })
            .catch((response) => {
                reject({ status: response.status, msg: response.statusText });
            });
    });
}

export function htmlDecode(input: string) {
    const doc = new DOMParser().parseFromString(input, "text/html");
    return doc.documentElement.textContent ?? "";
}

export function getRandomInt(max: number) {
    return Math.floor(Math.random() * max);
}

/**
 * Simple string templating, replaces strings of the form
 *     ${key}
 * with
 *      ctx[key]
 * and returns the result.
 *
 * @param template String to apply templates to
 * @param ctx Dictionary of key / values
 * @returns The processed string.
 */
export function applyTemplates(template: string, ctx: any) {
    let str = `${template}`;
    for (const [key, value] of Object.entries(ctx)) {
        str = str.replace(key, value as string);
    }
    return str;
}

export function copyToClipboard(text: string | undefined) {
    if (text) {
        navigator.clipboard.writeText(text);
    }
}

export function levenshteinDistance(str1 = "", str2 = "") {
    const track = Array(str2.length + 1)
        .fill(null)
        .map(() => Array(str1.length + 1).fill(null));
    for (let i = 0; i <= str1.length; i += 1) {
        track[0][i] = i;
    }
    for (let j = 0; j <= str2.length; j += 1) {
        track[j][0] = j;
    }
    for (let j = 1; j <= str2.length; j += 1) {
        for (let i = 1; i <= str1.length; i += 1) {
            const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
            track[j][i] = Math.min(
                track[j][i - 1] + 1, // deletion
                track[j - 1][i] + 1, // insertion
                track[j - 1][i - 1] + indicator // substitution
            );
        }
    }
    return track[str2.length][str1.length];
}

export function downloadToFile(content: string, contentType: string, filename?: string) {
    const file = new Blob([content], { type: contentType });
    return downloadBlobToFile(file, filename);
}

export function downloadBlobToFile(file: Blob, filename?: string) {
    return downloadUrlToFile(URL.createObjectURL(file), filename);
}

export function downloadUrlToFile(url: string, filename?: string) {
    const a = document.createElement("a") as any;
    a.download = filename;
    a.href = url;
    a.click();

    URL.revokeObjectURL(a.href);
}

// Return array of string values, or NULL if CSV string not well formed.
export function CSVtoArray(text: string) {
    const re_valid =
        /^\s*(?:'[^'\\]*(?:\\[\S\s][^'\\]*)*'|"[^"\\]*(?:\\[\S\s][^"\\]*)*"|[^,'"\s\\]*(?:\s+[^,'"\s\\]+)*)\s*(?:,\s*(?:'[^'\\]*(?:\\[\S\s][^'\\]*)*'|"[^"\\]*(?:\\[\S\s][^"\\]*)*"|[^,'"\s\\]*(?:\s+[^,'"\s\\]+)*)\s*)*$/;
    const re_value =
        /(?!\s*$)\s*(?:'([^'\\]*(?:\\[\S\s][^'\\]*)*)'|"([^"\\]*(?:\\[\S\s][^"\\]*)*)"|([^,'"\s\\]*(?:\s+[^,'"\s\\]+)*))\s*(?:,|$)/g;

    if (!re_valid.test(text)) {
        // Return NULL if input string is not well formed CSV string.
        return null;
    }
    const a = [];
    text.replace(re_value, function (_, m1, m2, m3) {
        if (m1 !== undefined) {
            // Remove backslash from \' in single quoted values.
            a.push(m1.replace(/\\'/g, "'"));
        } else if (m2 !== undefined) {
            // Remove backslash from \" in double quoted values.
            a.push(m2.replace(/\\"/g, '"'));
        } else if (m3 !== undefined) {
            a.push(m3);
        }
        return ""; // Return empty string.
    });
    // Handle special case of empty last value.
    if (/,\s*$/.test(text)) {
        a.push("");
    }
    return a;
}

export function toRadians(degrees: number) {
    return (degrees * Math.PI) / 180;
}

// Converts from radians to degrees.
export function toDegrees(radians: number) {
    return (radians * 180) / Math.PI;
}

export function hashCode(str: string): number {
    let hash = 0,
        i,
        chr;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export function openInMapsApp(place: IPlace | undefined) {
    if (place) {
        const queryParams = `daddr=${place.data.location.lat},${place.data.location.lon}&amp;ll=`;
        if (
            /* if we're on iOS, open in Apple Maps */
            navigator.platform.indexOf("iPhone") != -1 ||
            navigator.platform.indexOf("iPad") != -1 ||
            navigator.platform.indexOf("iPod") != -1
        ) {
            window.open(`maps://maps.google.com/maps?${queryParams}`);
        } else {
            /* else use Google */
            window.open(`https://maps.google.com/maps?${queryParams}`);
        }
    }
}

export function isValidUrl(urlString: string): boolean {
    return (
        urlString.startsWith("blob:") ||
        urlString.startsWith("http:") ||
        urlString.startsWith("https:")
    );
}

/**
 * Returns a modified copy of the passed object.
 */
export function modify(obj: any, props: any) {
    const updated = clone(obj);
    for (const prop in props) {
        updated[prop] = props[prop];
    }
    return updated;
}

export function getEnumValues(enumType: any) {
    return Object.keys(enumType)
        .map((x) => enumType[x])
        .filter((x) => isNaN(Number(x)));
}

export function shareContent(title: string, text: string, url: string) {
    if (!navigator.share) {
        return;
    }

    navigator
        .share({ title, text, url })
        .then(() => {
            console.log("The content was shared successfully");
        })
        .catch((error) => {
            console.error("Error sharing the content", error);
        });
}

export async function forceReloadPage() {
    const url = `${window.location.protocol}//${window.location.host}/`;

    await fetch(url, {
        headers: {
            cache: "reload",
        },
    });
    window.location.href = url;
    window.location.reload();
}

// From https://stackoverflow.com/a/12900504/9056439
export function getExtension(path: string): string {
    const basename = path.split(/[\\/]/).pop();
    if (basename) {
        // (supports `\\` and `/` separators)
        const pos = basename.lastIndexOf("."); // get last position of `.`

        if (basename !== "" && pos > 0) {
            return basename.slice(pos + 1); // extract extension ignoring `.`
        }
    }
    return "";
}

export function capitalizeFirstLetter(s: string) {
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

export function stringNotNullOrEmpty(s: string | undefined | null) {
    if (s) {
        return s.length > 0;
    }
    return false;
}

/**
 * Throttles an async function in a way that can be awaited.
 * By default throttle doesn't return a promise for async functions unless it's invoking them immediately. See CUR-4769 for details.
 * @param func async function to throttle calls for.
 * @param wait same function as lodash.throttle's wait parameter.
 *             Call this function at most this often.
 * @returns a promise which will be resolved/ rejected only if the function is executed, with the result of the underlying call.
 */
export function makeAsyncThrottle<F extends (...args: any[]) => Promise<any>>(
    func: F,
    wait?: number
) {
    const throttled = throttle((resolve, reject, args: Parameters<F>) => {
        func(...args)
            .then(resolve)
            .catch(reject);
    }, wait);
    return (...args: Parameters<F>): ReturnType<F> =>
        new Promise((resolve, reject) => {
            throttled(resolve, reject, args);
        }) as ReturnType<F>;
}

export function isWritable<T extends object>(obj: T, key: keyof T) {
    const desc = Object.getOwnPropertyDescriptor(obj, key) || {};
    return Boolean(desc.writable);
}

export function changeFileExtension(filename: string, newExtension: string): string {
    // Ensure the new extension starts with a dot
    if (!newExtension.startsWith(".")) {
        newExtension = "." + newExtension;
    }
    const lastDotIndex = filename.lastIndexOf(".");
    // If no dot is found, just append the new extension
    if (lastDotIndex === -1) {
        return filename + newExtension;
    }
    // Remove the old extension and add the new one
    return filename.substring(0, lastDotIndex) + newExtension;
}
